import TextareaAutosize from 'react-textarea-autosize';
import PeerId from 'peer-id';
import React, { useCallback, useEffect, useState } from 'react';
import { useRequest } from './useRequest';

const Types: Record<string, PeerId.CreateOptions> = {
  "Ed25519": { keyType: 'Ed25519' },
  "RSA1024": { keyType: 'RSA', bits: 1024 },
  "RSA2048": { keyType: 'RSA', bits: 2048 },
}

async function generateData(type: string) {
  const peerId = await PeerId.create(Types[type]);
  return {
    type,
    peerId,
    ...peerId.toJSON()
  }
}

type Data = ReturnType<typeof generateData> extends Promise<infer U> ? U : never;

const useMountEffect = (fun: React.EffectCallback) => {
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(fun, [])
}

const App: React.FC = () => {
  const { send, response, loading } = useRequest(generateData);
  const [history, setHistory] = useState("");
  const [type, setType] = useState("Ed25519");
  const [data, setData] = useState<Data>();

  const select = useCallback((t: React.MouseEvent<HTMLInputElement | HTMLTextAreaElement, MouseEvent>) => {
    const target = t.target as HTMLInputElement | HTMLTextAreaElement;
    target.select();
  }, []);

  const regenerate = useCallback(() => {
    send(type)
  }, [send, type])

  const clear = useCallback(() => {
    setHistory("");
    regenerate();
  }, [regenerate])

  useMountEffect(() => regenerate())

  useEffect(() => {
    if (response && data !== response) {
      setData(response);
      const r = response
      setHistory((history) => {
        return history + JSON.stringify({
          type: r.type,
          id: r.id,
          publicKey: r.pubKey,
          privKey: r.privKey
        }) + "\n"
      })
    }
  }, [response, data])

  return (
    <div className="App">
      <fieldset>
        <legend>IPFS PeerId Generator</legend>
        <dl>
          <dt>Type</dt>
          <dd>
            <div>
              {Object.keys(Types).map((i) => {
                return <label key={i}><input type="radio" name="type" checked={type === i} onClick={() => setType(i)} />{i}</label>
              })}
            </div>
          </dd>
        </dl>
        <button onClick={regenerate} disabled={loading}>Regenerate</button>
      </fieldset>

      <fieldset>
        <legend>result</legend>
        <dl>
          <dt>id</dt>
          <dd><TextareaAutosize className="auto" wrap={"soft"} readOnly onClick={select} value={data && data.id}></TextareaAutosize></dd>
          <dt>privKey</dt>
          <dd><TextareaAutosize className="auto" wrap={"soft"} readOnly onClick={select} value={data && data.privKey}></TextareaAutosize></dd>
          <dt>pubKey</dt>
          <dd><TextareaAutosize className="auto" wrap={"soft"} readOnly onClick={select} value={data && data.pubKey}></TextareaAutosize></dd>
        </dl>
      </fieldset>

      <fieldset>
        <legend>history: JSON.stringify({"{"}type, id, privKey, pubKey{"}"})</legend>
        <button onClick={clear}>Clear</button>
        <textarea readOnly wrap="off" rows={16} value={history}></textarea>
      </fieldset>

      <fieldset>
        <legend>links</legend>
        <ul>
          <li><a href="https://www.npmjs.com/package/peer-id" rel="nofollow noopener noreferrer">{"npm - peer-id: IPFS Peer ID implementation in JavaScript."}</a></li>
          <li><a href="https://github.com/whyrusleeping/ipfs-key" rel="nofollow noopener noreferrer">{"ipfs-key: A tool for easily generating ipfs keypairs"}</a></li>
        </ul>
      </fieldset>
    </div>
  );
}

export default App;
